import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import styles from "../../AddEvent.module.scss";
import { withTranslation } from "react-i18next";
import { useGetOfficeAgenda } from "repositories/scheduler-repository";
import * as moment from "moment";
import constants from "../../../../../constants.js";
import Loader from "components/Loader";
import HolidayImage from "../../../../../assets/images/early_on.svg";
import defautUserImage from "../../../../../assets/images/staff-default.svg";
import { Col } from "reactstrap";
import { Row } from "reactstrap";
import Text from "components/Text";
import { findKey } from "lodash";
import { renderOfficeNameOrEnterprise } from "../../addEvents.functions";
import { useHistory } from "react-router-dom";
import { encodeId } from "utils";
import {
  filterEventsBySearch,
  leaveClasses,
} from "accountOwner/pages/Scheduler/scheduler.function";
import { useParams } from "react-router-dom";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

const SchedulingTable = forwardRef(function SchedulingTable(
  {
    t,
    selectedStaff,
    selectedOwnerId,
    eventDate,
    agendaTypes = [],
    currentUserId,
    viewSettingFilter,
    cachedEventFilter,
    searchValue = "",
    showFreeSlots = false,
    startTime,
    endTime,
    onFreeSlotClick,
    onTimeLabelClick,
    selectedHourIdx,
    isFreeStotLoading,
    canShowOfficeName,
  },
  ref
) {
  const history = useHistory();
  const { eventId: eventIdFromUrl } = useParams();
  const { profile, isOwner, dataAccessValues, isStaff } =
    useAccountOwnerCommon();
  const canEditAndViewOthersData = useCallback(
    (officeId) => {
      if (isStaff) {
        return (
          dataAccessValues?.officeList?.some(
            (res) => res.officeId === officeId && res.isAdmin
          ) || false
        );
      }
      return isOwner;
    },
    [isStaff, isOwner, dataAccessValues?.officeList]
  );
  // Restore: Generate time labels from 12 AM to 11 PM (24-hour schedule)
  const times = Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 === 0 ? 12 : i % 12;
    const period = i < 12 ? "AM" : "PM";
    return `${hour} ${period}`;
  });

  const enableFetch = Boolean(selectedOwnerId && eventDate);
  const startDate = moment(eventDate).format("YYYY-MM-DD");
  const endDate = moment(eventDate).format("YYYY-MM-DD");
  const userIds = selectedStaff.map((item) => item.userId);

  const {
    isLoading,
    data: weeklyData,
    refetch,
  } = useGetOfficeAgenda(selectedOwnerId, startDate, endDate, [], userIds, {
    enabled: enableFetch,
  });

  const [agendaFilter, setAgendaFilter] = useState([]);

  // Ref for the table container
  const tableContainerRef = useRef(null);
  // Ref for the current hour row
  const currentHourRowRef = useRef(null);

  // Get the hour to scroll to: use startTime if present, else current hour
  let scrollHour = null;
  if (startTime) {
    scrollHour = moment.isMoment(startTime)
      ? startTime.hour()
      : moment(startTime, ["h:mm A", "HH:mm"]).hour();
  } else {
    scrollHour = moment().hour();
  }

  const filterByEvents = (agenda) => {
    return (agenda || []).map((item) => {
      const schedulerList = Array.isArray(item.officeSchedulerList)
        ? item.officeSchedulerList
        : [item.officeSchedulerList];

      const filteredSchedulerList = schedulerList.map((schedulerItem) => {
        let eventsCreatedByUser = [];
        let eventsNotCreatedByUser = [];
        let filteredSchedulerEvents = [];
        let shiftsCreatedByUser = [];
        let shiftsCreatedByOthers = [];
        let eventsNotAccepted = [];
        let shiftsNotAccepted = [];

        if (agendaTypes?.length > 0) {
          // Events
          if (agendaTypes.includes(0)) {
            eventsCreatedByUser =
              schedulerItem?.schedulerEvents?.filter(
                (event) =>
                  !event?.isShift && event?.createdById === currentUserId
              ) || [];
          }
          if (agendaTypes.includes(-1)) {
            eventsNotCreatedByUser =
              schedulerItem?.schedulerEvents?.filter(
                (event) =>
                  !event?.isShift && event?.createdById !== currentUserId
              ) || [];
          }
          // Events Not Accepted (type 4)
          if (agendaTypes.includes(4)) {
            eventsNotAccepted =
              schedulerItem?.schedulerEvents?.filter(
                (event) =>
                  !event?.isShift &&
                  event?.eventStatus === constants.SCHEDULERSTATUS.PENDING
              ) || [];
          }
          // Shifts
          if (agendaTypes.includes(8)) {
            shiftsCreatedByUser =
              schedulerItem?.schedulerEvents?.filter(
                (event) =>
                  event?.isShift && event?.createdById === currentUserId
              ) || [];
          }
          if (agendaTypes.includes(6)) {
            shiftsCreatedByOthers =
              schedulerItem?.schedulerEvents?.filter(
                (event) =>
                  event?.isShift && event?.createdById !== currentUserId
              ) || [];
          }
          // Shifts Not Accepted (type 7)
          if (agendaTypes.includes(7)) {
            shiftsNotAccepted =
              schedulerItem?.schedulerEvents?.filter(
                (event) =>
                  event?.isShift &&
                  event?.eventStatus === constants.SCHEDULERSTATUS.PENDING
              ) || [];
          }

          filteredSchedulerEvents =
            item?.schedulerEvents?.filter((event) => {
              // If only event filters are selected, exclude shifts
              if (
                agendaTypes.includes(4) && // Events Not Accepted
                !agendaTypes.includes(7) // Shifts Not Accepted not selected
              ) {
                return agendaTypes.includes(event.agendaType) && !event.isShift;
              }
              // If only shift filters are selected, exclude events
              if (
                agendaTypes.includes(7) && // Shifts Not Accepted
                !agendaTypes.includes(4) // Events Not Accepted not selected
              ) {
                return agendaTypes.includes(event.agendaType) && event.isShift;
              }
              // Otherwise, include all matching agendaTypes
              return agendaTypes.includes(event.agendaType);
            }) || [];
        }

        // Combine all event arrays and deduplicate by event id
        let combinedEvents = [
          ...eventsCreatedByUser,
          ...eventsNotCreatedByUser,
          ...eventsNotAccepted,
          ...shiftsCreatedByUser,
          ...shiftsCreatedByOthers,
          ...shiftsNotAccepted,
          ...filteredSchedulerEvents,
        ];
        const uniqueEventsMap = {};
        combinedEvents.forEach((event) => {
          if (event && event.id) {
            uniqueEventsMap[event.id] = event;
          }
        });
        const dedupedEvents = Object.values(uniqueEventsMap);

        return {
          ...schedulerItem,
          schedulerEvents: dedupedEvents,
          appointments:
            schedulerItem?.appointments?.filter((event) =>
              agendaTypes.includes(event.agendaType)
            ) || [],
          appointmentSlots:
            schedulerItem?.appointmentSlots?.filter((event) =>
              agendaTypes.includes(event.agendaType)
            ) || [],
          leaves:
            schedulerItem?.leaves?.filter((event) =>
              agendaTypes.includes(event.agendaType)
            ) || [],
          schedulerBusySlots: (
            schedulerItem?.schedulerBusySlots?.filter((event) =>
              agendaTypes.includes(event.agendaType)
            ) || []
          ).concat(
            schedulerItem?.appointmentSlots?.filter((event) =>
              agendaTypes.includes(constants.agendaType.BUSY_SLOT)
            ) || []
          ),
        };
      });

      return {
        ...item,
        officeSchedulerList: filteredSchedulerList,
      };
    });
  };

  useEffect(() => {
    if (weeklyData?.data) {
      let filtered = weeklyData.data;
      // filtered = filterAgendaBySelectedStaff(filtered);
      if (cachedEventFilter?.length > 0) {
        filtered = filterByEvents(filtered);
      }
      setAgendaFilter(filtered);
    }
  }, [
    weeklyData,
    JSON.stringify(selectedStaff),
    JSON.stringify(cachedEventFilter),
  ]);

  // Scroll to the selected hour row after data is loaded or startTime changes
  useEffect(() => {
    if (tableContainerRef.current && currentHourRowRef.current) {
      setTimeout(() => {
        currentHourRowRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [agendaFilter, selectedStaff, eventDate, startTime]);

  // Popup logic (simplified for daily view)
  const [popupEvent, setPopupEvent] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupAnchorRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const anchor = popupAnchorRef.current;
      const container = document.querySelector(".table-fix");

      if (anchor && popupEvent?._type !== "leave" && container) {
        const containerRect = container.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();

        setPopupPosition({
          top: anchorRect.bottom - containerRect.top + container.scrollTop + 8,
          left: anchorRect.left - containerRect.left + container.scrollLeft,
        });
      }
      // const popupBox = document.querySelector(".event-date-popup");
      // if (popupBox) {
      //   popupBox.scrollIntoView({ behavior: "smooth", block: "nearest" });
      //   popupBox.focus?.();
      // }
    };

    if (popupEvent) {
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      setTimeout(updatePosition, 0);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [popupEvent]);

  // Close popup when data or filters change
  useEffect(() => {
    closePopup();
  }, [agendaFilter, selectedStaff, eventDate, cachedEventFilter, searchValue]);

  const handleEventClick = (event, anchor, staffName, mouseEvent) => {
    // If the event is created by the current user and is EVENT or BUSY_SLOT, go to details instead of opening popup
    if (
      currentUserId === event.createdById &&
      (event.agendaType === constants.agendaType.EVENT ||
        event.agendaType === constants.agendaType.BUSY_SLOT)
    ) {
      goToDetails(event.id, event.agendaType, true, eventDate);
      return;
    }
    setPopupEvent({ ...event, staffName: staffName });

    const container = document.querySelector(".table-fix");

    if (anchor && container) {
      const containerRect = container.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();

      setPopupPosition({
        top: anchorRect.bottom - containerRect.top + container.scrollTop + 8,
        left: anchorRect.left - containerRect.left + container.scrollLeft,
      });

      popupAnchorRef.current = anchor;
    }
  };

  const closePopup = () => {
    setPopupEvent(null);
  };

  const goToDetails = (id, type, createdByMe, requestedDate) => {
    if (!id || !type) return;
    const schedulingAssistantState = {
      selectedStaff,
      eventDate,
      // Add more state if needed
    };
    history.replace({
      state: {
        requestedDate,
        fromScreen: "schedulingAssistant",
        schedulingAssistantState,
        eventId: eventIdFromUrl,
      },
    });
    switch (type) {
      case constants.agendaType.EVENT: {
        let pathname = "/";
        pathname = createdByMe
          ? constants.routes.scheduler.eventDetails.replace(
              ":eventId",
              encodeId(id)
            )
          : constants.routes.scheduler.eventDetailsOnly.replace(
              ":eventId",
              encodeId(id)
            );
        history.push({
          pathname,
          state: {
            requestedDate,
            fromScreen: "schedulingAssistant",
            schedulingAssistantState,
            eventId: eventIdFromUrl,
          },
        });
        break;
      }
      case constants.agendaType.BUSY_SLOT: {
        const pathname = constants.routes.scheduler.busySlotDetail.replace(
          ":busySlotId",
          encodeId(id)
        );
        history.push({
          pathname,
          state: {
            requestedDate,
            fromScreen: "schedulingAssistant",
            schedulingAssistantState,
            eventId: eventIdFromUrl,
          },
        });
        break;
      }
      default: {
        return;
      }
    }
  };

  const getPopupData = (item, requestedDate) => {
    const data = {};
    data.title = item?.title || item?.reasonForBlock || null;
    data.officeName = renderOfficeNameOrEnterprise(item, t);
    data.patientFullName = item?.patientFullName || null;
    data.startTime = moment(item?.startTime).format("h:mm a");
    data.endTime = moment(item?.endTime).format("h:mm a");
    data.date = moment(requestedDate).format("MMM D, YYYY [-] ddd");
    const { agendaType } = item;
    switch (agendaType) {
      case constants.agendaType.EVENT: {
        const createdByOthers =
          item?.isShift && profile?.id !== item?.createdById;
        if (createdByOthers && canEditAndViewOthersData(item?.officeId)) {
          data.tag = t("scheduler.agenda.shiftByOthers");
        } else if (item?.isShift) {
          data.tag = t("scheduler.agenda.shiftTag");
        } else {
          data.tag = t("scheduler.agenda.eventTag");
        }
        break;
      }
      case constants.agendaType.BUSY_SLOT: {
        data.tag = t("scheduler.agenda.busyTag");
        break;
      }
      case constants.agendaType.BLOCKED: {
        data.tag = t("scheduler.agenda.busyTag");
        break;
      }
      case constants.agendaType.APPOINTMENT: {
        data.tag = t("scheduler.agenda.appointmentTag");
        data.title = t("scheduler.agenda.patient", {
          name: item.patientFullName,
        });
        data.isAppointment = true;
        const status = findKey(
          constants.appointmentStatus,
          (s) => s === item.appointmentStatus
        );
        data.appointmentStatus = status
          ? t(`appointmentStatus.${status}`)
          : "--";
        break;
      }
      case constants.agendaType.LEAVE: {
        const { text } = leaveClasses(item?.statusId);
        data.tag = t(text);
        data.title = "";
        data.isLeave = true;
        break;
      }
      default: {
        break;
      }
    }
    return data;
  };

  const getPopupView = (
    item,
    backgroudColor,
    tagBackgroundColor,
    requestedDate,
    staffName
  ) => {
    const data = getPopupData(item, requestedDate);

    return (
      <div className={styles["scheduler-popup-box"] + " " + "event-date-popup"}>
        <span className={styles["close-icon"]} onClick={closePopup}>
          &times;
        </span>
        <div
          className={
            styles["scheduler-event-box"] + " " + styles[backgroudColor]
          }
        >
          <div className="d-flex align-items-center">
            <div
              className={styles["event-tag"] + " " + styles[tagBackgroundColor]}
            >
              {data.tag}
            </div>
            <div className={styles["event-type"] + " " + "pl-2 break_all"}>
              {data.title}
            </div>
          </div>
          <Row className={styles.row}>
            <Col sm="6">
              <Text size="10px" marginBottom="0px" weight="400" color="#87928D">
                {constants.agendaType.APPOINTMENT === item.agendaType
                  ? t("scheduler.agenda.doctorName")
                  : t("scheduler.agenda.staffName")}
              </Text>
              <Text
                size="12px"
                marginBottom="10px"
                weight="500"
                color="#2F3245"
                wordBreak="break-all"
              >
                {staffName}
              </Text>
            </Col>
            <Col sm="6">
              <Text size="10px" marginBottom="0px" weight="400" color="#87928D">
                {t("accountOwner.officeName")}
              </Text>
              <Text
                size="12px"
                marginBottom="10px"
                weight="500"
                color="#2F3245"
                wordBreak="break-all"
              >
                {data.officeName}
              </Text>
            </Col>
            <Col sm="6">
              <Text size="10px" marginBottom="0px" weight="400" color="#87928D">
                {t("accountOwner.date")}
              </Text>
              <Text
                size="12px"
                marginBottom="10px"
                weight="500"
                color="#2F3245"
                wordBreak="break-all"
              >
                {data.date}
              </Text>
            </Col>
            {!data.isLeave && (
              <Col sm="6">
                <Text
                  size="10px"
                  marginBottom="0px"
                  weight="400"
                  color="#87928D"
                >
                  {data.isAppointment
                    ? t("timeOfAppointment")
                    : t("staff.startTime")}
                </Text>
                <Text
                  size="12px"
                  marginBottom="12px"
                  weight="500"
                  color="#2F3245"
                  wordBreak="break-all"
                >
                  {data.startTime}
                </Text>
              </Col>
            )}
            {!data.isLeave && (
              <Col sm="6">
                <Text
                  size="10px"
                  marginBottom="0px"
                  weight="400"
                  color="#87928D"
                >
                  {data.isAppointment ? t("status") : t("staff.endTime")}
                </Text>
                <Text
                  size="12px"
                  marginBottom="12px"
                  weight="500"
                  color="#2F3245"
                  wordBreak="break-all"
                >
                  {data.isAppointment ? data?.appointmentStatus : data.endTime}
                </Text>
              </Col>
            )}
          </Row>
        </div>
      </div>
    );
  };

  let backgroundClass = "";
  let tagBackgroundClass = "";

  if (popupEvent) {
    if (popupEvent.agendaType == 4) {
      if (popupEvent.eventStatus === 1) {
        backgroundClass = "color-blue";
        tagBackgroundClass = "theme-green-bg";
      } else if (currentUserId === popupEvent.createdById) {
        backgroundClass = "";
        tagBackgroundClass = "theme-green-bg";
      } else {
        backgroundClass = "color-grey";
        tagBackgroundClass = "theme-green-bg";
      }
    } else if (popupEvent.agendaType == 5 || popupEvent.agendaType == 3) {
      backgroundClass = "light-orange-bg";
      tagBackgroundClass = "dark-orange-bg";
    } else if (popupEvent.agendaType == 2) {
      const { Highlighter, main } = leaveClasses(popupEvent?.statusId);
      backgroundClass = main;
      tagBackgroundClass = Highlighter;
    } else {
      backgroundClass = "light-purple-bg";
      tagBackgroundClass = "dark-grey-bg";
    }
  }

  useImperativeHandle(ref, () => ({
    refetch,
  }));

  const calculateOverlapPositions = (events) => {
    events.sort(
      (a, b) => getTimeInMinutes(a.startTime) - getTimeInMinutes(b.startTime)
    );

    const positions = [];

    for (let i = 0; i < events.length; i++) {
      let overlaps = [events[i]];
      for (let j = i + 1; j < events.length; j++) {
        const endA = getTimeInMinutes(events[i].endTime);
        const startB = getTimeInMinutes(events[j].startTime);

        if (startB < endA) {
          overlaps.push(events[j]);
        }
      }

      const totalOverlap = overlaps.length;
      overlaps.forEach((event, index) => {
        positions.push({
          event,
          widthFactor: 1 / totalOverlap,
          leftOffsetFactor: index / totalOverlap,
        });
      });

      i += overlaps.length - 1;
    }

    return positions;
  };

  const getTimeInMinutes = (time) => {
    if (!time) {
      return 0;
    }

    const date = new Date(time);
    if (isNaN(date.getTime())) {
      console.error("Invalid date format:", time);
      return 0;
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();
    return hours * 60 + minutes;
  };

  const getAllStaffEvents = (scheduler) => {
    const events = [];

    // Add scheduler events
    if (scheduler.schedulerEvents) {
      events.push(
        ...scheduler.schedulerEvents.map((event) => ({
          ...event,
          type: "schedulerEvent",
        }))
      );
    }

    // Add appointment slots
    if (scheduler.appointmentSlots) {
      events.push(
        ...scheduler.appointmentSlots.map((slot) => ({
          ...slot,
          type: "appointmentSlot",
          title: slot.reasonForBlock || "Appointment Slot",
        }))
      );
    }

    // Add busy slots
    if (scheduler.schedulerBusySlots) {
      events.push(
        ...scheduler.schedulerBusySlots.map((slot) => ({
          ...slot,
          type: "busySlot",
          isBusySlot: true,
          title: slot.title || "Busy",
        }))
      );
    }

    // Add appointments
    if (scheduler.appointments) {
      events.push(
        ...scheduler.appointments.map((appointment) => ({
          ...appointment,
          type: "appointment",
          title: appointment.description || "Appointment",
        }))
      );
    }

    // Add leaves
    if (scheduler.leaves) {
      events.push(
        ...scheduler.leaves.map((leave) => ({
          ...leave,
          type: "leave",
          isAllDayEvent: true, // Ensure leaves are treated as all-day events
          title: leave.reason || "Leave",
        }))
      );
    }
    // Sort events by start time
    return events.sort((a, b) => {
      const timeA = getTimeInMinutes(a.startTime);
      const timeB = getTimeInMinutes(b.startTime);
      return timeA - timeB;
    });
  };

  const groupEventsByStartTime = (events) => {
    const grouped = {};

    events.forEach((event) => {
      const startMinutes = getTimeInMinutes(event.startTime);
      if (!grouped[startMinutes]) {
        grouped[startMinutes] = [];
      }
      grouped[startMinutes].push(event);
    });

    return grouped;
  };

  const getEventDisplayText = (event) => {
    const timeString = event?.isAllDayEvent
      ? t("scheduler.agenda.allDayEvent")
      : moment(event?.startTime).format("h:mm A") +
        " - " +
        moment(event?.endTime).format("h:mm A");
    return (
      <div className="d-flex ml-2">
        {(!viewSettingFilter.length ||
          viewSettingFilter.includes("timeString")) && (
          <div className={styles["event-date"]}>
            {timeString}({event?.timezoneCode})
          </div>
        )}
        {canShowOfficeName &&
          (!viewSettingFilter.length ||
            viewSettingFilter.includes("officeName")) && (
            <div className={styles["event-office"]}>{event.officeName}</div>
          )}
      </div>
    );
  };

  const categorizeEvents = (events) => {
    return {
      allDayEvents: events.filter(
        (event) =>
          event.isAllDayEvent ||
          (event.startTime &&
            event.endTime &&
            new Date(event.startTime).toDateString() !==
              new Date(event.endTime).toDateString())
      ),
      timeEvents: events.filter(
        (event) =>
          !event.isAllDayEvent &&
          event.startTime &&
          event.endTime &&
          new Date(event.startTime).toDateString() ===
            new Date(event.endTime).toDateString()
      ),
    };
  };

  // Update findCommonAvailabilitySlots to use 1-minute increments
  const findCommonAvailabilitySlots = (staffList) => {
    // Convert each staff's schedule into busy time slots
    const busySlots = staffList.map((staff) => {
      const allEvents = getAllStaffEvents(staff);
      return allEvents.map((event) => ({
        start: getTimeInMinutes(event.startTime),
        end: getTimeInMinutes(event.endTime),
      }));
    });

    // Find all possible time slots (in 1-minute increments)
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 1) {
        const startTime = hour * 60 + minute;
        const endTime = startTime + 1;
        timeSlots.push({ start: startTime, end: endTime });
      }
    }

    // Find slots where all staff are free
    const commonAvailability = timeSlots.filter((slot) => {
      return !busySlots.some((staffBusySlots) =>
        staffBusySlots.some(
          (busySlot) =>
            // Fixed boundary condition: slot overlaps if it touches or crosses event boundaries
            slot.start < busySlot.end && slot.end > busySlot.start
        )
      );
    });

    // Merge consecutive slots
    const mergedSlots = [];
    let currentSlot = null;

    commonAvailability.forEach((slot) => {
      if (!currentSlot) {
        currentSlot = { ...slot };
      } else if (slot.start === currentSlot.end) {
        currentSlot.end = slot.end;
      } else {
        mergedSlots.push(currentSlot);
        currentSlot = { ...slot };
      }
    });

    if (currentSlot) {
      mergedSlots.push(currentSlot);
    }

    // Filter slots that are at least 1 minute long
    return mergedSlots.filter((slot) => slot.end - slot.start >= 1);
  };

  const getEventColor = (event, index) => {
    const pendingCheck = event?.statusId === constants.agendaType.PENDINGLEAVE;

    switch (event.agendaType) {
      case 2: // Leave
        return pendingCheck ? "#fdeded" : "rgba(113, 213, 185, 0.2)";
      case 3: // Busy
      case 5: // Blocked
        return "#fef3d7";
      case 4: // Event
        if (event.eventStatus === 1) {
          return "#e1f4fd";
        } else if (currentUserId === event.createdById) {
          return "#f9fcee";
        } else {
          return "#f2f3ef";
        }
      default: // Appointment
        return "#f6f1fd";
    }
  };

  const getEventBorderColor = (event) => {
    const pendingCheck = event?.statusId === constants.agendaType.PENDINGLEAVE;
    switch (event.agendaType) {
      case 2: // Leave
        return pendingCheck ? "#ff5757" : "#71d5b9";
      case 3: // Busy
      case 5: // Blocked
        return "#f7cb7a";
      case 4: // Event
        if (event.eventStatus === 1) {
          return "#82d8f8";
        } else if (currentUserId === event.createdById) {
          return "#f7cb7a";
        } else {
          return "#707070";
        }
      default: // Appointment
        return "#d8b4f8";
    }
  };

  const officeSchedulerList = agendaFilter?.[0]?.officeSchedulerList || [];

  const HOUR_CELL_HEIGHT_PX = 60; // Assumed fixed height for each hour row
  const MIN_EVENT_HEIGHT_PX = 8; // Minimum height for any event

  return (
    <div className={styles.tableContainer} ref={tableContainerRef}>
      {(isLoading || isFreeStotLoading) && <Loader />}
      <table className={styles.table}>
        <thead className={styles.scheduling_table_head}>
          <tr className={styles.headerRow}>
            <th className={styles.firstCol}>
              <p className={styles.empty_tag}></p>
            </th>{" "}
            {officeSchedulerList?.map((staff) => (
              <th
                className={styles.tableCell}
                key={staff.user?.id || staff.id}
                style={{ borderTop: "none" }}
              >
                <div className={`${styles.table_header_new}`}>
                  <div className={`${styles.client_img}`}>
                    <img
                      src={staff.user?.profilePic || defautUserImage}
                      alt={`${staff?.user?.firstName || ""} ${
                        staff?.user?.lastName || ""
                      }`}
                      onError={(e) => {
                        e.currentTarget.src = defautUserImage;
                      }}
                    />
                  </div>
                  <span>
                    {staff?.user?.firstName || ""} {staff?.user?.lastName || ""}
                  </span>
                </div>
              </th>
            ))}
          </tr>
          <tr
            className={styles.headerRow}
            style={{ backgroundColor: "white", height: "43px" }}
          >
            <th
              className={`${styles.firstCol} ${styles.all_day_event_header} ${styles.all_day_text} + " " p-0 border-0`}
            >
              <p>all-day</p>
            </th>
            {officeSchedulerList.map((scheduler, colIdx) => {
              const allEvents = getAllStaffEvents(scheduler);
              // Apply search filter to all-day events
              const allDayEvents = filterEventsBySearch(
                allEvents.filter((event) => event.isAllDayEvent),
                scheduler.user,
                searchValue
              );
              return (
                <td
                  className={styles.tableCell}
                  key={colIdx}
                  style={{ height: "43px", borderTop: "none" }}
                >
                  <div
                    className={`${styles.table_cell_data_container} ${styles.table_cell_all_day}`}
                  >
                    {Object.entries(groupEventsByStartTime(allDayEvents)).map(
                      ([startMinutesStr, group], idx) => {
                        const overlapPositions =
                          calculateOverlapPositions(group);

                        return overlapPositions.map(({ event }, index) => {
                          const eventColor = getEventColor(event, index);
                          const borderColor = getEventBorderColor(event);
                          const createdByOthers =
                            event?.isShift &&
                            profile?.id !== event?.createdById;
                          return (
                            <div
                              className={styles.overlap_position_event}
                              style={{
                                backgroundColor: eventColor,
                                width: 100 / overlapPositions.length + "%",
                              }}
                              onClick={(e) => {
                                if (
                                  (currentUserId === event.createdById &&
                                    (event.agendaType ===
                                      constants.agendaType.EVENT ||
                                      event.agendaType ===
                                        constants.agendaType.BUSY_SLOT)) ||
                                  (createdByOthers && canEditAndViewOthersData(event?.officeId))
                                ) {
                                  goToDetails(
                                    event.id,
                                    event.agendaType,
                                    true,
                                    eventDate
                                  );
                                } else {
                                  handleEventClick(
                                    event,
                                    e.currentTarget,
                                    `${scheduler?.user?.firstName} ${scheduler?.user?.lastName}`,
                                    e
                                  );
                                }
                              }}
                            >
                              <div
                                className={styles.event_display_text}
                                style={{
                                  // backgroundColor: eventColor,
                                  borderLeft: `2px solid ${borderColor}`,
                                }}
                              >
                                {getEventDisplayText(event)}
                              </div>
                            </div>
                          );
                        });
                      }
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {/* Holiday Row */}
          {agendaFilter?.[0]?.holidayList?.length > 0 && (
            <tr>
              <td className={`${styles.firstCol}`}></td>
              <td
                colSpan={selectedStaff?.length}
                className={`p-0 ${styles.holiday_cell} ${
                  agendaFilter?.[0]?.holidayList?.length > 0
                    ? styles["light-red-bg"]
                    : styles["no-bg-theme"]
                }`}
              >
                <div className={styles.holiday_title}>
                  <img
                    src={HolidayImage}
                    alt="holiday"
                    className="account_owner_image mr-2"
                    style={{ height: 24, verticalAlign: "middle" }}
                  />
                  <span>{agendaFilter[0].holidayList[0].title}</span>
                </div>
              </td>
            </tr>
          )}
          {times.map((time, rowIdx) => (
            <tr
              className={styles.row}
              key={time}
              ref={rowIdx === scrollHour ? currentHourRowRef : null}
            >
              <td
                className={styles.firstCol}
                onClick={() => {
                  if (typeof onTimeLabelClick === "function")
                    onTimeLabelClick(rowIdx);
                }}
                style={{
                  borderLeft: "1px solid #ddd",
                  borderRight: "1px solid #ddd",
                  ...(selectedHourIdx === rowIdx
                    ? {
                        borderTop: "2px solid #a9cf3d",
                        borderBottom: "2px solid #a9cf3d",
                        borderLeft: "2px solid #a9cf3d",
                      }
                    : {}),
                  cursor: "pointer",
                  position: "relative",
                }}
              >
                <p className={styles.ptext}>{time}</p>
                {startTime &&
                  (() => {
                    const startHour = moment.isMoment(startTime)
                      ? startTime.hour()
                      : moment(startTime, ["h:mm A", "HH:mm"]).hour();
                    const startMinute = moment.isMoment(startTime)
                      ? startTime.minute()
                      : moment(startTime, ["h:mm A", "HH:mm"]).minute();

                    if (rowIdx === startHour) {
                      const topPosition = (startMinute / 60) * 100;
                      return (
                        <div
                          className={styles.time_indicator_container}
                          style={{
                            top: `${topPosition}%`,
                          }}
                        >
                          <div className={styles.time_indicator_circle} />
                          <div className={styles.time_indicator_line} />
                        </div>
                      );
                    }
                  })()}
              </td>
              {officeSchedulerList?.map((scheduler, colIdx) => {
                const isLast = colIdx === officeSchedulerList.length - 1;
                let cellStyle = {
                  position: "relative",
                  height: "100%",
                  borderLeft: "1px solid #ddd",
                  borderRight: "1px solid #ddd",
                  overflow: "hidden",
                };
                if (selectedHourIdx === rowIdx) {
                  cellStyle.borderTop = "2px solid #a9cf3d";
                  cellStyle.borderBottom = "2px solid #a9cf3d";
                  if (isLast) {
                    cellStyle.borderRight = "2px solid #a9cf3d";
                  }
                }
                const allEvents = getAllStaffEvents(scheduler);
                // Apply search filter to time events
                const { timeEvents } = categorizeEvents(allEvents);
                const filteredTimeEvents = filterEventsBySearch(
                  timeEvents,
                  scheduler.user,
                  searchValue
                );
                const commonAvailabilitySlots = showFreeSlots
                  ? findCommonAvailabilitySlots(officeSchedulerList)
                  : [];

                return (
                  <td
                    key={colIdx}
                    className={`${styles.tableCell} ${
                      agendaFilter?.[0]?.holidayList?.length > 0
                        ? styles["light-red-bg"]
                        : styles["no-bg-theme"]
                    }`}
                    style={cellStyle}
                    /**Intetionally commented the code for the future requirement */
                    // onClick={(e) => {
                    //   if (!showFreeSlots && onFreeSlotClick) {
                    //     const HOUR_CELL_HEIGHT_PX = 60; // Adjust if your cell height is different
                    //     const hourStart = moment(eventDate)
                    //       .startOf("day")
                    //       .add(rowIdx, "hours");
                    //     const hourEnd = moment(eventDate)
                    //       .startOf("day")
                    //       .add(rowIdx + 1, "hours");
                    //     const hourStartMinutes =
                    //       hourStart.hours() * 60 + hourStart.minutes();
                    //     let hourEndMinutes =
                    //       hourEnd.hours() * 60 + hourEnd.minutes();
                    //     if (rowIdx === 23) {
                    //       hourEndMinutes = 23 * 60 + 59; // 11:59 PM
                    //     }
                    //     // Get all events, using only time
                    //     const events = getAllStaffEvents(scheduler)
                    //       .filter((e) => e.startTime && e.endTime)
                    //       .map((e) => ({
                    //         startMinutes:
                    //           moment(e.startTime).hours() * 60 +
                    //           moment(e.startTime).minutes(),
                    //         endMinutes:
                    //           moment(e.endTime).hours() * 60 +
                    //           moment(e.endTime).minutes(),
                    //       }))
                    //       .filter(
                    //         (e) =>
                    //           e.startMinutes < hourEndMinutes &&
                    //           e.endMinutes > hourStartMinutes
                    //       );

                    //     // Build busy intervals within the hour, sorted
                    //     const busy = events
                    //       .map((e) => ({
                    //         start: Math.max(hourStartMinutes, e.startMinutes),
                    //         end: Math.min(hourEndMinutes, e.endMinutes),
                    //       }))
                    //       .filter((e) => e.end > e.start)
                    //       .sort((a, b) => a.start - b.start);

                    //     // Build free gaps
                    //     const freeGaps = [];
                    //     let prevEnd = hourStartMinutes;
                    //     for (const b of busy) {
                    //       if (b.start > prevEnd) {
                    //         freeGaps.push({ start: prevEnd, end: b.start });
                    //       }
                    //       prevEnd = Math.max(prevEnd, b.end);
                    //     }
                    //     if (prevEnd < hourEndMinutes) {
                    //       freeGaps.push({
                    //         start: prevEnd,
                    //         end: hourEndMinutes,
                    //       });
                    //     }

                    //     // Special case for last hour: cap end at 11:59 PM
                    //     if (rowIdx === 23) {
                    //       freeGaps.forEach((gap) => {
                    //         if (gap.end === 1440) gap.end = 23 * 60 + 59;
                    //       });
                    //     }

                    //     // If no free gaps, do nothing
                    //     if (freeGaps.length === 0) return;

                    //     // Determine which gap was clicked based on vertical position
                    //     const y = e.nativeEvent.offsetY;
                    //     const cellHeight =
                    //       e.currentTarget.offsetHeight || HOUR_CELL_HEIGHT_PX;
                    //     const clickMinute =
                    //       hourStartMinutes + Math.floor((y / cellHeight) * 60);

                    //     // Find the gap that contains the click
                    //     const selectedGap = freeGaps.find(
                    //       (gap) =>
                    //         clickMinute >= gap.start && clickMinute < gap.end
                    //     );

                    //     if (!selectedGap) return;

                    //     const slotStart = moment(eventDate)
                    //       .startOf("day")
                    //       .add(selectedGap.start, "minutes");
                    //     const slotEnd = moment(eventDate)
                    //       .startOf("day")
                    //       .add(selectedGap.end, "minutes");
                    //     onFreeSlotClick(slotStart.toDate(), slotEnd.toDate());
                    //   }
                    // }}
                  >
                    {/* Common availability slots - only show when showFreeSlots is true */}
                    {showFreeSlots &&
                      commonAvailabilitySlots.map((slot, index) => {
                        // Calculate the start and end of the current hour in minutes
                        const hourStart = rowIdx * 60;
                        const hourEnd = (rowIdx + 1) * 60;
                        // Check if the slot overlaps with the current hour cell
                        if (slot.start < hourEnd && slot.end > hourStart) {
                          // Calculate the effective start and end within this hour
                          const effectiveStart = Math.max(
                            slot.start,
                            hourStart
                          );
                          const effectiveEnd = Math.min(slot.end, hourEnd);
                          const top = ((effectiveStart - hourStart) / 60) * 100;
                          let height =
                            ((effectiveEnd - effectiveStart) / 60) * 100;

                          // Minimum height logic for available slot
                          let slotHeightPx =
                            (height / 100) * HOUR_CELL_HEIGHT_PX;
                          if (slotHeightPx < MIN_EVENT_HEIGHT_PX) {
                            slotHeightPx = MIN_EVENT_HEIGHT_PX;
                          }
                          const heightPercent =
                            (slotHeightPx / HOUR_CELL_HEIGHT_PX) * 100;

                          // Calculate effective start and end dates for the slot click handler
                          const hourCellStartMoment = moment(eventDate)
                            .startOf("day")
                            .add(rowIdx, "hours");
                          const slotStartMoment = moment(eventDate)
                            .startOf("day")
                            .add(slot.start, "minutes");
                          const hourCellEndMoment = moment(eventDate)
                            .startOf("day")
                            .add(rowIdx + 1, "hours");
                          const slotEndMoment = moment(eventDate)
                            .startOf("day")
                            .add(slot.end, "minutes");
                          const effectiveStartDate = moment
                            .max(hourCellStartMoment, slotStartMoment)
                            .toDate();
                          const effectiveEndDate = moment
                            .min(slotEndMoment, hourCellEndMoment)
                            .toDate();

                          return (
                            <div
                              key={`availability-${index}`}
                              style={{
                                position: "absolute",
                                top: `${top}%`,
                                left: 2,
                                right: 2,
                                height: `${heightPercent}%`,
                                borderRadius: 6,
                                backgroundColor: "rgba(144, 238, 144, 0.3)",
                                border: "1px solid rgba(144, 238, 144, 0.5)",
                                zIndex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#2D3245",
                                cursor: onFreeSlotClick ? "pointer" : "default",
                              }}
                              onClick={() => {
                                if (onFreeSlotClick) {
                                  onFreeSlotClick(
                                    effectiveStartDate,
                                    effectiveEndDate // Use the actual end of the available slot
                                  );
                                }
                              }}
                            >
                              {t("availableSlot")}
                            </div>
                          );
                        }
                        return null;
                      })}

                    {/* Events for this hour */}
                    {Object.entries(
                      groupEventsByStartTime(filteredTimeEvents)
                    ).map(([startMinutesStr, group]) => {
                      const overlapPositions = calculateOverlapPositions(group);

                      return overlapPositions.map(
                        ({ event, widthFactor, leftOffsetFactor }, index) => {
                          // Calculate event position within the hour
                          const startMinutes = getTimeInMinutes(
                            event.startTime
                          );
                          const endMinutes = getTimeInMinutes(event.endTime);
                          const hourStartMinutes = rowIdx * 60;
                          const hourEndMinutes = (rowIdx + 1) * 60;

                          // Check if event overlaps with this hour
                          if (
                            !(
                              startMinutes < hourEndMinutes &&
                              endMinutes > hourStartMinutes
                            )
                          ) {
                            return null;
                          }

                          // Calculate position within the hour cell as percentages
                          const eventStartInHour = Math.max(
                            startMinutes,
                            hourStartMinutes
                          );
                          const eventEndInHour = Math.min(
                            endMinutes,
                            hourEndMinutes
                          );

                          const top =
                            ((eventStartInHour - hourStartMinutes) / 60) * 100;
                          const height =
                            ((eventEndInHour - eventStartInHour) / 60) * 100;

                          // Calculate pixel values
                          let eventHeightPx =
                            (height / 100) * HOUR_CELL_HEIGHT_PX;

                          let topPx = (top / 100) * HOUR_CELL_HEIGHT_PX;
                          if (eventHeightPx < MIN_EVENT_HEIGHT_PX) {
                            // Adjust height to minimum
                            eventHeightPx = MIN_EVENT_HEIGHT_PX;
                            // Prevent overflow if event is at the bottom of the cell
                            if (eventHeightPx + topPx > HOUR_CELL_HEIGHT_PX) {
                              eventHeightPx = HOUR_CELL_HEIGHT_PX - topPx;
                            }
                          }
                          const heightPercent =
                            (eventHeightPx / HOUR_CELL_HEIGHT_PX) * 100;

                          const topPercent =
                            (topPx / HOUR_CELL_HEIGHT_PX) * 100;

                          // Check if this is the first hour of the event (for showing label)
                          const eventStartHour = Math.floor(startMinutes / 60);
                          const isFirstHourOfEvent = eventStartHour === rowIdx;

                          // Check if event continues to next hour (no bottom spacing)
                          const continuesNextHour = endMinutes > hourEndMinutes;

                          // Check if event continues from previous hour (no top spacing)
                          const continuesFromPrevHour =
                            startMinutes < hourStartMinutes;

                          // Get event colors based on type
                          const eventColor = getEventColor(event, index);
                          const borderColor = getEventBorderColor(event);

                          // Calculate width and position as percentages of the cell
                          const eventWidthPercent = widthFactor * 100 - 1; // Subtract 1% for spacing
                          const leftOffsetPercent = leftOffsetFactor * 100;
                          const createdByOthers =
                            event?.isShift &&
                            profile?.id !== event?.createdById;

                          return (
                            <div
                              key={`${event.id}-${index}-${rowIdx}`}
                              style={{
                                position: "absolute",
                                top: `${topPercent}%`,
                                left: `${leftOffsetPercent}%`,
                                width: `${eventWidthPercent}%`,
                                height: `${height}%`,
                                borderRadius:
                                  continuesFromPrevHour || continuesNextHour
                                    ? 0
                                    : 6,
                                borderTopLeftRadius: continuesFromPrevHour
                                  ? 0
                                  : 6,
                                borderTopRightRadius: continuesFromPrevHour
                                  ? 0
                                  : 6,
                                borderBottomLeftRadius: continuesNextHour
                                  ? 0
                                  : 6,
                                borderBottomRightRadius: continuesNextHour
                                  ? 0
                                  : 6,
                                zIndex: 2,
                                overflow: "hidden",
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                if (
                                  (currentUserId === event.createdById &&
                                    (event.agendaType ===
                                      constants.agendaType.EVENT ||
                                      event.agendaType ===
                                        constants.agendaType.BUSY_SLOT)) ||
                                  (createdByOthers && canEditAndViewOthersData(event?.officeId))
                                ) {
                                  goToDetails(
                                    event.id,
                                    event.agendaType,
                                    true,
                                    eventDate
                                  );
                                } else {
                                  handleEventClick(
                                    event,
                                    e.currentTarget,
                                    `${scheduler?.user?.firstName} ${scheduler?.user?.lastName}`,
                                    e
                                  );
                                }
                              }}
                            >
                              {/* Left border */}
                              <div
                                style={{
                                  position: "absolute",
                                  left: 2,
                                  top: continuesFromPrevHour ? 0 : 3,
                                  bottom: continuesNextHour ? 0 : 3,
                                  width: 2,
                                  backgroundColor: borderColor,
                                  zIndex: 3,
                                }}
                              />
                              {/* Background */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: eventColor,
                                  opacity: 0.5,
                                }}
                              />
                              {/* Content - only show in first hour of event */}
                              {isFirstHourOfEvent && (
                                <div className={styles.first_hour_event}>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: "#2D3245",
                                      opacity: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      width: "100%",
                                    }}
                                  >
                                    {getEventDisplayText(event)}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }
                      );
                    })}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Popup for event details, rendered outside the table and positioned absolutely */}
      {popupEvent && (
        <div
          style={{
            position: "absolute",
            top: popupPosition.top,
            left: popupPosition.left,
            zIndex: 2,
          }}
        >
          {getPopupView(
            popupEvent,
            backgroundClass,
            tagBackgroundClass,
            eventDate,
            popupEvent?.staffName
          )}
        </div>
      )}
    </div>
  );
});

export default withTranslation()(SchedulingTable);
